import React, { useMemo } from 'react';

import { SENDING_TYPE_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { AttentionTriangleIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, Tag, SettingsItemWrapper } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FrameAnalysisFrequencyModal } from '@vlabs/pages/video-analytics/streams/components/modal/FrameAnalysisFrequencyModal';

import st from './AdvancedSettingsSubForm.module.sass';
import { DEFAULT_ADVANCED_CONFIG } from './defaultValues.ts';

const commonStylesProps = {
  className: st.SettingItem,
  titleClassName: st.SettingItem__Title,
  contentClassName: st.SettingItem__Content,
};

const intValidation = {
  min: validate.gte(1),
  validate: { number: validate.integer() },
};

const numValidation = {
  min: validate.gte(0),
  validate: { number: validate.number() },
};

export function AdvancedSettingsSubForm({ onCancel, onSave, fieldName = '' }) {
  const form = useFormContext();

  const { t } = useTranslation();
  const createFullName = (name) => (fieldName ? `${fieldName}.${name}` : name);

  const rateUnitValue = useWatch({ control: form.control, name: createFullName('parameters.rate.unit') })?.value;
  const ratePeriodValidationRules = useMemo(
    () => (rateUnitValue === 'frame' ? intValidation : numValidation),
    [rateUnitValue],
  );

  const onReset = () => {
    const currentValues = form.getValues();

    form.reset({
      ...currentValues,
      parameters: DEFAULT_ADVANCED_CONFIG,
    });
  };

  return (
    <div className={st.Root}>
      <div className={st.TitleBlock}>
        <h5 className={st.Title}>
          {t('checks:video check form.advanced settings.title.label')}
          <Tag
            className={st.Tag}
            data-testid="workspace.analyticEventDetails.tag"
            icon={<AttentionTriangleIcon />}
            shape="square"
            size="large"
            value="ADVANCED"
          />
        </h5>
        <div className={st.TitleHint}>
          {t('checks:video check form.advanced settings.titleHint.label')}
        </div>
      </div>

      <div className={st.FormContainer}>
        <div className={st.FormBlock}>
          <div className={st.Subtitle}>
            {t('checks:video check form.advanced settings.videoParams.title.label')}
          </div>

          <div className={st.FormGroup}>
            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.videoParams.frameAnalysisType.label')}
              {...commonStylesProps}
            >
              <Control.Select
                control={form.control}
                name={createFullName('parameters.rate.unit')}
                options={SENDING_TYPE_OPTIONS.raw}
              />
            </SettingsItemWrapper>

            <SettingsItemWrapper
              data-tooltip-content={t('videoAnalytics:form.rate.period.tooltip')}
              modalContent={<FrameAnalysisFrequencyModal />}
              title={t('checks:video check form.advanced settings.videoParams.frameAnalysisFrequency.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.rate.period'), ratePeriodValidationRules)}
              />
            </SettingsItemWrapper>

            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.videoParams.maxFaceAttributeFrames.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.face_samples.count'), {
                  min: validate.gte(0),
                  validate: {
                    number: validate.integer(),
                  },
                })}
              />
            </SettingsItemWrapper>

            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.videoParams.trackCreationFrames.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.probe_count'), {
                  min: validate.gte(0),
                  validate: {
                    number: validate.integer(),
                  },
                })}
              />
            </SettingsItemWrapper>
          </div>
        </div>

        <div className={st.FormBlock}>
          <div className={st.Subtitle}>
            {t('checks:video check form.advanced settings.filters.title.label')}
          </div>

          <div className={st.FormGroup}>
            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.filters.minDetectionQuality.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.face_samples.filters.score'), {
                  min: validate.gte(0),
                  max: validate.lte(1),
                  validate: {
                    number: validate.number(),
                  },
                })}
              />
            </SettingsItemWrapper>

            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.filters.headYaw.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.face_samples.filters.head_yaw'), {
                  min: validate.gte(0),
                  max: validate.lte(90),
                  validate: {
                    number: validate.integer(),
                  },
                })}
              />
            </SettingsItemWrapper>

            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.filters.headRoll.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.face_samples.filters.head_roll'), {
                  min: validate.gte(0),
                  max: validate.lte(90),
                  validate: {
                    number: validate.integer(),
                  },
                })}
              />
            </SettingsItemWrapper>

            <SettingsItemWrapper
              title={t('checks:video check form.advanced settings.filters.headPitch.label')}
              {...commonStylesProps}
            >
              <Control.Input
                errors={form.formState.errors}
                {...form.register(createFullName('parameters.face_samples.filters.head_pitch'), {
                  min: validate.gte(0),
                  max: validate.lte(90),
                  validate: {
                    number: validate.integer(),
                  },
                })}
              />
            </SettingsItemWrapper>
          </div>
        </div>

        <div className={st.Action}>
          <Control.Button
            data-testid="submit btn"
            onClick={onSave}
          >
            {t('shared:btn.save')}
          </Control.Button>

          <Control.Button
            data-testid="cancel btn"
            kind="secondary"
            onClick={onReset}
          >
            {t('checks:video check form.advanced settings.actions.resetToDefaults.label')}
          </Control.Button>

          <Control.Button
            data-testid="cancel btn"
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
          >
            {t('shared:btn.cancel')}
          </Control.Button>
        </div>
      </div>
    </div>

  );
}

AdvancedSettingsSubForm.propTypes = {
  fieldName: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
